import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Paper,
  Breadcrumbs,
  Tooltip,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Toolbar,
  InputAdornment,
  OutlinedInput,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { FC, useState, useRef, useCallback, useMemo } from 'react';

import { TDocument, TDocumentStatus } from '@/types/document';
import { TDocumentFolder } from '@/types/document-folder';

import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import {
  useGetFolderHierarchy,
  useGetFolderContents,
  useGetRootContents,
  useMoveDocumentsToFolder,
} from '@/api/document-folder';
import { useDeleteVaultDocument } from '@/api/endpoints/vault/useDeleteVaultDocument';
import { ArrowRightIcon } from '@/assets/icons/ArrowRightIcon';
import { DocumentIcon } from '@/assets/icons/DocumentIcon';
import { File07Icon } from '@/assets/icons/File07Icon';
import { FolderIcon } from '@/assets/icons/FolderIcon';
import { FolderPlusIcon } from '@/assets/icons/FolderPlusIcon';
import { FolderShieldIcon } from '@/assets/icons/FolderShieldIcon';
import { Image01Icon } from '@/assets/icons/Image01Icon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';

import DocumentViewModal from '@/components/document/DocumentViewModal';

import CreateFolderModal from './CreateFolderModal';

type Order = 'asc' | 'desc';

interface SortableItem {
  id: string | number;
  name: string;
  created_at: string;
  size?: number;
  _count?: { documents: number };
  type: 'folder' | 'document';
  mime_type?: string;
  original_name?: string;
  is_system_folder?: boolean;
  file_path?: string;
  status?: string;
  created_by: string;
  updated_at: string;
  lastModifiedDate?: string | null;
  documentCount?: number;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] === null || b[orderBy] === undefined) return -1;
  if (a[orderBy] === null || a[orderBy] === undefined) return 1;

  if (typeof a[orderBy] === 'string') {
    const aValue = String(a[orderBy]).toLowerCase();
    const bValue = String(b[orderBy]).toLowerCase();
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }

  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<T>(order: Order, orderBy: keyof T) {
  return order === 'desc'
    ? (a: T, b: T) => descendingComparator(a, b, orderBy)
    : (a: T, b: T) => -descendingComparator(a, b, orderBy);
}

interface FolderViewProps {
  currentFolderId: number | null;
  propertyLocationId?: number;
  onFolderChange?: (folderId: number | null) => void;
}

const FolderView: FC<FolderViewProps> = ({
  currentFolderId,
  propertyLocationId,
  onFolderChange,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof SortableItem>('name');
  const [filterName, setFilterName] = useState('');

  const { data: hierarchy = [], isLoading: isHierarchyLoading } = useGetFolderHierarchy(
    currentFolderId || 0,
    {
      enabled: !!currentFolderId,
    }
  );

  const { data: rootContents, isLoading: isRootLoading } = useGetRootContents(
    { property_location_id: propertyLocationId ? Number(propertyLocationId) : undefined },
    { enabled: !currentFolderId && !!propertyLocationId }
  );

  const { data: folderContents, isLoading: isFolderLoading } = useGetFolderContents(
    currentFolderId ? Number(currentFolderId) : 0,
    { property_location_id: propertyLocationId ? Number(propertyLocationId) : undefined },
    { enabled: !!currentFolderId }
  );

  const contents = currentFolderId ? folderContents : rootContents;
  const isLoading = isHierarchyLoading || isRootLoading || isFolderLoading;

  // Document preview state
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [previewDocumentCategoryId, setPreviewDocumentCategoryId] = useState<number | null>(null);
  const clickTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Drag and drop state
  const [draggedDocumentId, setDraggedDocumentId] = useState<string | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<number | null>(null);

  const moveDocumentsMutation = useMoveDocumentsToFolder();

  // Add new state for delete dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);

  // Add delete document mutation
  const deleteDocumentMutation = useDeleteVaultDocument({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folders', 'contents', currentFolderId] });
      queryClient.invalidateQueries({ queryKey: ['folders', 'root-contents'] });
    },
  });

  // Add new state for create folder modal
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);

  // Add a check for whether we're in a system folder
  const isInSystemFolder = useMemo(() => {
    if (!currentFolderId) return false;
    const currentFolder = hierarchy[hierarchy.length - 1];
    return currentFolder?.is_system_folder ?? false;
  }, [currentFolderId, hierarchy]);

  const handleSort = (property: keyof SortableItem) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
  };

  const sortedItems = useMemo(() => {
    if (!contents) return [];

    const allItems: SortableItem[] = [
      ...(contents.folders?.map(folder => ({
        id: folder.id,
        name: folder.name,
        created_at: folder.created_at,
        _count: folder._count,
        documentCount: folder._count?.documents || 0,
        type: 'folder' as const,
        is_system_folder: folder.is_system_folder,
        created_by: folder.created_by,
        updated_at: folder.updated_at,
        lastModifiedDate: folder.lastModifiedDate,
      })) || []),
      ...(contents.documents?.map(doc => ({
        id: doc.id,
        name: doc.original_name,
        created_at: doc.created_at,
        size: doc.size,
        documentCount: 0,
        type: 'document' as const,
        mime_type: doc.mime_type,
        original_name: doc.original_name,
        file_path: doc.file_path,
        status: doc.status,
        created_by: doc.created_by,
        updated_at: doc.updated_at,
      })) || []),
    ];

    let filtered = allItems;

    // Apply name filter
    if (filterName) {
      filtered = filtered.filter((item: SortableItem) =>
        item.name.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    return filtered.sort(getComparator(order, orderBy));
  }, [contents, order, orderBy, filterName]);

  const handleFolderClick = (folderId: number | null) => {
    onFolderChange?.(folderId);
  };

  const handleDocumentClick = useCallback((document: TDocument) => {
    if (clickTimerRef.current === null) {
      clickTimerRef.current = setTimeout(() => {
        setPreviewDocumentId(document.id);
        setPreviewDocumentCategoryId(document.category?.id ? Number(document.category.id) : null);
        clickTimerRef.current = null;
      }, 200);
    }
  }, []);

  const handleClosePreview = () => {
    setPreviewDocumentId(null);
    setPreviewDocumentCategoryId(null);
  };

  // Drag and drop handlers
  const handleDocumentDragStart = (event: React.DragEvent, documentId: string) => {
    event.dataTransfer.setData('application/terrai-document', documentId);
    setDraggedDocumentId(documentId);
  };

  const handleDocumentDragEnd = () => {
    setDraggedDocumentId(null);
    setDragOverFolderId(null);
  };

  const handleFolderDragOver = (event: React.DragEvent, folder: TDocumentFolder) => {
    if (event.dataTransfer.types.includes('application/terrai-document')) {
      event.preventDefault();
      setDragOverFolderId(folder.id);
    }
  };

  const handleFolderDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOverFolderId(null);
  };

  const handleFolderDrop = async (event: React.DragEvent, folder: TDocumentFolder) => {
    event.preventDefault();
    setDragOverFolderId(null);

    const documentId = event.dataTransfer.getData('application/terrai-document');
    if (documentId) {
      try {
        await moveDocumentsMutation.mutateAsync({
          document_ids: [documentId],
          folder_id: folder.id,
        });

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['folders', 'contents', currentFolderId],
          }),
          queryClient.invalidateQueries({
            queryKey: ['folders', 'root-contents'],
          }),
        ]);

        enqueueSnackbar('Document moved successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar((error as Error).message || 'Failed to move document', {
          variant: 'error',
        });
      }
    }
  };

  // Helper function to determine border color
  const getBorderColor = (folder: TDocumentFolder, isDragOver: boolean) => {
    if (!isDragOver) return 'transparent';
    return folder.is_system_folder ? 'error.main' : 'primary.main';
  };

  // Helper to format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Add delete handlers
  const handleDeleteClick = useCallback((event: React.MouseEvent, documentId: string) => {
    event.stopPropagation();
    setDocumentToDelete(documentId);
    setOpenDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = () => {
    if (documentToDelete) {
      deleteDocumentMutation.mutate({ documentId: documentToDelete });
    }
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setDocumentToDelete(null);
  };

  // Add handlers for create folder modal
  const handleOpenCreateFolderModal = () => {
    setIsCreateFolderModalOpen(true);
  };

  const handleCloseCreateFolderModal = () => {
    setIsCreateFolderModalOpen(false);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  // const itemsCount = sortedItems.length;

  return (
    <MuiPageWrapper>
      <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
        {/* Header with breadcrumbs and actions */}
        <Paper elevation={0} sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Breadcrumbs separator={<ArrowRightIcon />} sx={{ flexGrow: 1 }}>
              <Button
                variant="text"
                onClick={() => handleFolderClick(null)}
                startIcon={<FolderIcon />}
                size="small"
                sx={{ borderRadius: 2 }}
              >
                My Vault
              </Button>
              {hierarchy.map((folder: TDocumentFolder, index: number) => {
                const isLast = index === hierarchy.length - 1;
                if (isLast) {
                  return (
                    <Typography key={folder.id} color="primary" variant="body1" fontWeight={600}>
                      {folder.name}
                    </Typography>
                  );
                }
                return (
                  <Button
                    key={folder.id}
                    variant="text"
                    onClick={() => handleFolderClick(folder.id)}
                    startIcon={<FolderIcon />}
                    size="small"
                    sx={{ borderRadius: 2 }}
                  >
                    {folder.name}
                  </Button>
                );
              })}
            </Breadcrumbs>
            <Tooltip
              title={
                isInSystemFolder
                  ? 'Cannot create folders inside system folders'
                  : 'Create new folder'
              }
            >
              <span>
                <IconButton
                  color="primary"
                  onClick={handleOpenCreateFolderModal}
                  disabled={isInSystemFolder}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'grey.300',
                      color: 'grey.500',
                    },
                  }}
                >
                  <FolderPlusIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Paper>

        <Box sx={{ flex: 1 }}>
          <Toolbar
            sx={{
              height: 64,
              display: 'flex',
              justifyContent: 'space-between',
              p: theme.spacing(2),
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'common.white',
            }}
          >
            <OutlinedInput
              fullWidth
              value={filterName}
              onChange={handleFilterName}
              placeholder="Search files and folders..."
              startAdornment={
                <InputAdornment position="start">
                  <SearchMdIcon sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
              sx={{
                maxWidth: 320,
                bgcolor: 'common.white',
              }}
            />
          </Toolbar>

          {/* Table View */}
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              border: '0px solid',
              borderColor: 'divider',
              bgcolor: 'common.white',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.secondary[200] }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textTransform: 'none',
                      minWidth: 400,
                      '&:hover': { bgcolor: `${theme.palette.secondary[200]} !important` },
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textTransform: 'none',
                      minWidth: 120,
                      '&:hover': { bgcolor: `${theme.palette.secondary[200]} !important` },
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'type'}
                      direction={orderBy === 'type' ? order : 'asc'}
                      onClick={() => handleSort('type')}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textTransform: 'none',
                      minWidth: 160,
                      '&:hover': { bgcolor: `${theme.palette.secondary[200]} !important` },
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'lastModifiedDate'}
                      direction={orderBy === 'lastModifiedDate' ? order : 'asc'}
                      onClick={() => handleSort('lastModifiedDate')}
                    >
                      Last Modified
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textTransform: 'none',
                      minWidth: 80,
                      '&:hover': { bgcolor: `${theme.palette.secondary[200]} !important` },
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'documentCount'}
                      direction={orderBy === 'documentCount' ? order : 'asc'}
                      onClick={() => handleSort('documentCount')}
                    >
                      Files
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textTransform: 'none',
                      minWidth: 100,
                      '&:hover': { bgcolor: `${theme.palette.secondary[200]} !important` },
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'size'}
                      direction={orderBy === 'size' ? order : 'asc'}
                      onClick={() => handleSort('size')}
                    >
                      File Size
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left" sx={{ width: 60 }} /> {/* Actions column */}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 8 }}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <FolderIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                          {filterName
                            ? 'No items found matching your search.'
                            : 'This folder is empty'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" textAlign="center">
                          {filterName
                            ? 'Try adjusting your search terms.'
                            : 'Drag and drop documents here or create a new folder to get started'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedItems.map(item => {
                    if (item.type === 'folder') {
                      const folder: TDocumentFolder = {
                        id: item.id as number,
                        name: item.name,
                        created_at: item.created_at,
                        _count: { documents: item._count?.documents || 0, sub_folders: 0 },
                        is_system_folder: item.is_system_folder || false,
                        created_by: item.created_by,
                        updated_at: item.updated_at,
                        description: '',
                        parent_folder_id: undefined,
                        property_location_id: undefined,
                      };
                      return (
                        <TableRow
                          key={`folder-${folder.id}`}
                          onClick={() => handleFolderClick(folder.id)}
                          onDragOver={e => handleFolderDragOver(e, folder)}
                          onDragLeave={handleFolderDragLeave}
                          onDrop={e => handleFolderDrop(e, folder)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor:
                              dragOverFolderId === folder.id ? alpha('#2196f3', 0.08) : 'inherit',
                            transition: 'all 0.2s ease-in-out',
                            border:
                              dragOverFolderId === folder.id
                                ? '2px dashed'
                                : '2px solid transparent',
                            borderColor: getBorderColor(folder, dragOverFolderId === folder.id),
                            '&:hover': {
                              backgroundColor: `${theme.palette.secondary[200]} !important`,
                              '& .MuiTableCell-root': {
                                color: 'inherit !important',
                              },
                              '& .MuiSvgIcon-root': {
                                color: 'inherit !important',
                              },
                            },
                            borderBottom: '1px solid',
                            borderBottomColor: 'divider',
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" spacing={2} alignItems="center">
                              {folder.is_system_folder ? <FolderShieldIcon /> : <FolderIcon />}
                              <Typography
                                variant="body3"
                                color="text.primary"
                                fontWeight={500}
                                sx={{
                                  maxWidth: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  '&:hover': {
                                    color: theme.palette.primary.main,
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                {folder.name}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body3" color="text.primary">
                              Folder
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body3" color="text.primary">
                              {formatDate(folder.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body3" color="text.primary">
                              {folder._count?.documents || 0}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body3" color="text.primary">
                              —
                            </Typography>
                          </TableCell>
                          <TableCell align="left" /> {/* Empty cell for actions */}
                        </TableRow>
                      );
                    }
                    const document: TDocument = {
                      id: item.id as string,
                      original_name: item.original_name || '',
                      created_at: item.created_at,
                      size: item.size || 0,
                      mime_type: item.mime_type || '',
                      file_path: item.file_path || '',
                      status: item.status as TDocumentStatus,
                      created_by: item.created_by,
                      updated_at: item.updated_at,
                      category: undefined,
                      category_id: undefined,
                      progress: undefined,
                    };
                    let FileIcon: React.ComponentType = DocumentIcon;
                    if (document.mime_type?.startsWith('image/')) {
                      FileIcon = Image01Icon;
                    } else if (document.mime_type === 'application/pdf') {
                      FileIcon = File07Icon;
                    }

                    return (
                      <TableRow
                        key={`document-${document.id}`}
                        onClick={() => handleDocumentClick(document)}
                        draggable
                        onDragStart={e => handleDocumentDragStart(e, document.id)}
                        onDragEnd={handleDocumentDragEnd}
                        sx={{
                          cursor: 'pointer',
                          opacity: draggedDocumentId === document.id ? 0.5 : 1,
                          '&:hover': {
                            backgroundColor: `${theme.palette.secondary[200]} !important`,
                            '& .MuiTableCell-root': {
                              color: 'inherit !important',
                            },
                            '& .MuiSvgIcon-root': {
                              color: 'inherit !important',
                            },
                          },
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <FileIcon />
                            <Tooltip title={document.original_name} placement="top">
                              <Typography
                                variant="body3"
                                color="text.primary"
                                fontWeight={500}
                                sx={{
                                  maxWidth: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  '&:hover': {
                                    color: theme.palette.primary.main,
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                {document.original_name}
                              </Typography>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body3" color="text.primary">
                            {document.mime_type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body3" color="text.primary">
                            {formatDate(document.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body3" color="text.primary">
                            —
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body3" color="text.primary">
                            {document.size ? `${Math.round(document.size / 1024)} KB` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <IconButton
                            size="small"
                            onClick={e => handleDeleteClick(e, document.id)}
                            sx={{
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.08),
                              },
                            }}
                          >
                            <Trash01Icon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Document Preview Modal */}
        {previewDocumentId && (
          <DocumentViewModal
            id={previewDocumentId}
            categoryId={previewDocumentCategoryId ?? undefined}
            onClose={handleClosePreview}
          />
        )}

        {/* Add Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          PaperProps={{
            sx: {
              minWidth: { xs: '90%', sm: 400 },
              maxWidth: { xs: '90%', sm: 500 },
            },
          }}
        >
          <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this document?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Create Folder Modal */}
        <CreateFolderModal
          open={isCreateFolderModalOpen}
          onClose={handleCloseCreateFolderModal}
          currentFolderId={currentFolderId}
          propertyLocationId={propertyLocationId}
        />
      </Stack>
    </MuiPageWrapper>
  );
};

export default FolderView;
